import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import type { Extension } from "@codemirror/state";

/*
 * Syntax highlighting driven by design tokens. Each entry pulls its color from
 * a CSS variable defined in tokens.json → generated-tokens.css, so light/dark
 * mode flips automatically via the body `.dark` class without re-creating the
 * editor.
 *
 * Mapping rationale — code languages (JSON + SQL):
 *   keyword         → SQL SELECT/FROM/etc.
 *   string/escape   → string literals in both JSON and SQL
 *   number          → numeric literals
 *   bool/null       → JSON true/false/null
 *   property name   → JSON object keys
 *   comment         → SQL -- comments
 *   punctuation/operator → fall back to default --color-fg
 *
 * Markdown (prose, not code) inverts the usual emphasis: the CONTENT carries
 * the styling and the syntax scaffolding recedes, which is why
 * `processingInstruction` (`##`, `**`, `-`, backticks) is muted and listed
 * LAST — a heading's `##` matches both rules, and the later one wins the tie.
 * Weight and color do the work; no font-size steps, because a taller line box
 * in a monospace editor drags the native caret's height with it.
 */
const codeEditorHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: "var(--color-syntax-keyword)" },
  { tag: t.controlKeyword, color: "var(--color-syntax-keyword)" },
  { tag: t.operatorKeyword, color: "var(--color-syntax-keyword)" },
  { tag: t.string, color: "var(--color-syntax-string)" },
  { tag: t.special(t.string), color: "var(--color-syntax-string)" },
  { tag: t.escape, color: "var(--color-syntax-string)" },
  { tag: t.number, color: "var(--color-syntax-number)" },
  { tag: t.bool, color: "var(--color-syntax-number)" },
  { tag: t.null, color: "var(--color-syntax-number)" },
  { tag: t.propertyName, color: "var(--color-syntax-property)" },
  { tag: t.comment, color: "var(--color-syntax-comment)", fontStyle: "italic" },
  {
    tag: t.lineComment,
    color: "var(--color-syntax-comment)",
    fontStyle: "italic",
  },
  {
    tag: t.blockComment,
    color: "var(--color-syntax-comment)",
    fontStyle: "italic",
  },

  // --- Markdown ---
  { tag: t.heading1, color: "var(--color-accent)", fontWeight: "600" },
  { tag: t.heading2, color: "var(--color-accent)", fontWeight: "600" },
  { tag: t.heading3, color: "var(--color-accent)", fontWeight: "500" },
  { tag: t.heading4, color: "var(--color-accent)", fontWeight: "500" },
  { tag: t.heading5, color: "var(--color-accent)", fontWeight: "500" },
  { tag: t.heading6, color: "var(--color-accent)", fontWeight: "500" },
  { tag: t.strong, fontWeight: "600" },
  { tag: t.emphasis, fontStyle: "italic" },
  {
    tag: t.strikethrough,
    color: "var(--color-fg-muted)",
    textDecoration: "line-through",
  },
  { tag: t.monospace, color: "var(--color-syntax-string)" },
  {
    tag: t.link,
    color: "var(--color-accent)",
    textDecoration: "underline",
  },
  { tag: t.url, color: "var(--color-accent)" },
  {
    tag: t.quote,
    color: "var(--color-fg-muted)",
    fontStyle: "italic",
  },
  { tag: t.contentSeparator, color: "var(--color-fg-muted)" },
  // LAST on purpose — see the header comment.
  { tag: t.processingInstruction, color: "var(--color-fg-muted)" },
]);

/*
 * Empty EditorView.theme() shims defeat CM6's `baseTheme()` light defaults
 * when the app is in dark mode. The `dark` flag flips which built-in theme
 * variants apply (e.g. selection bg). All actual styling comes from the
 * sibling CSS file driven by --color-* tokens.
 */
const lightShim = EditorView.theme({}, { dark: false });
const darkShim = EditorView.theme({}, { dark: true });

export function codeEditorTheme(isDark: boolean): Extension[] {
  return [
    isDark ? darkShim : lightShim,
    syntaxHighlighting(codeEditorHighlightStyle),
  ];
}
